import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { getAffiliateApi } from '../api/affiliate'
import type { Summary } from '../api/affiliate/types'
import { canSeeAffiliate } from '../lib/affiliate'
import { useSession } from './session'

/**
 * หลังบ้านพาร์ทเนอร์: is the page there for me (the server switch CHAT_AFFILIATE_ENABLED), and the rail's dot.
 * - `ensure()` asks GET /api/v1/affiliate/status once, for owner/head only (the reviewer and staff are never asked).
 *   404 = switched off: hidden, never asked again by itself. Another error: not shown, and not retried in a loop
 *   (the next sign-in / page open asks again).
 * - On and set up: GET summary for the dot (applications or withdrawal requests waiting; red once an application is
 *   late, while a paid shop order has not reached POS — Summary.shop.attention, P3 — or once a withdrawal request is past
 *   its 3 business days — Summary.payouts.late, P4 — or while the POS status feed is stale / stuck — Summary.ledger,
 *   P4). `refresh()` after any write.
 */
export type AffState = 'unknown' | 'loading' | 'hidden' | 'on' | 'error'

export const useAffiliate = defineStore('affiliate', () => {
  const session = useSession()
  const allowed = computed(() => canSeeAffiliate(session.me?.role, !!session.me?.reviewer))
  const state = ref<AffState>('unknown')
  const configured = ref(false)
  const summary = ref<Summary | null>(null)

  const visible = computed(() => allowed.value && state.value === 'on')
  const pending = computed(() => summary.value?.applications.pending ?? 0)
  /** open withdrawal requests (the ถอนเงิน tab's number); 0 from an affiliate before P4 */
  const payoutsOpen = computed(() => summary.value?.payouts?.requested ?? 0)
  /** paid shop orders not in POS (failed / stuck / charged twice); 0 from an affiliate before P3 */
  const attention = computed(() => summary.value?.shop?.attention ?? 0)
  /** withdrawal requests past their 3 business days (P4) */
  const payoutsLate = computed(() => summary.value?.payouts?.late ?? 0)
  /** open requests whose documents wait for the team's check before they can be paid (ruling R10) */
  const payoutsReview = computed(() => summary.value?.payouts?.docs_review ?? 0)
  /** the POS status feed has not caught a page for 10 minutes, or one order keeps failing: new commission stops (P4) */
  const feedProblem = computed(() => !!summary.value?.ledger && (summary.value.ledger.feed_stale || !!summary.value.ledger.feed_stuck_code))
  const alert = computed<'none' | 'dot' | 'crit'>(() => {
    if (attention.value > 0 || payoutsLate.value > 0 || feedProblem.value) return 'crit'
    const a = summary.value?.applications
    if (a && a.pending > 0 && a.late > 0) return 'crit'
    return (a?.pending ?? 0) > 0 || payoutsOpen.value > 0 ? 'dot' : 'none'
  })

  let sumSeq = 0
  async function refresh(): Promise<void> {
    if (!allowed.value || state.value !== 'on' || !configured.value) return
    const mine = ++sumSeq
    try {
      const s = await getAffiliateApi().summary()
      if (mine === sumSeq) summary.value = s
    } catch {
      /* keep the last one: the page shows its own error */
    }
  }

  let inflight: Promise<void> | null = null
  function ensure(): Promise<void> {
    if (!allowed.value) return Promise.resolve()
    if (state.value === 'on' || state.value === 'hidden') return Promise.resolve()
    if (inflight) return inflight
    state.value = 'loading'
    inflight = (async () => {
      try {
        const st = await getAffiliateApi().status()
        configured.value = !!st.configured
        state.value = st.enabled ? 'on' : 'hidden'
      } catch (e) {
        state.value = (e as { status?: number })?.status === 404 ? 'hidden' : 'error'
      } finally {
        inflight = null
      }
      if (state.value === 'on') await refresh()
    })()
    return inflight
  }

  return {
    state, configured, summary, visible, pending, payoutsOpen, payoutsLate, payoutsReview, feedProblem, attention, alert, ensure, refresh,
  }
})
